import { useCallback, useEffect } from 'react';
import type { MutableRefObject } from 'react';
import type { ClarificationResponse } from '../client/SmartAssistantBackendClient';
import type { SendMessageInternalResult } from './useSendMessageInternal';
import { ChatMessageModel } from "../client/SmartAssistantBackendClient";

type ChatQueueParams = {
  chatId: string | null;
  createChatId: () => string;
  setChatId: (id: string) => void;
  queuedMessagesRef: MutableRefObject<any[]>;
  nextQueuedIdRef: MutableRefObject<number>;
  setQueuedMessages: (updater: (prev: any[]) => any[]) => void;
  setClarificationsToSend: (value: ClarificationResponse | undefined) => void;
  setClarificationDrafts: (value: Record<string, string>) => void;
  setClarificationOpen: (value: boolean) => void;
  setClarificationKey: (value: string | null) => void;
  setText: (value: string) => void;
  setTextDraft: (value: string) => void;
  enqueueOutgoingRef: MutableRefObject<((payload: any) => void) | null>;
  awaitingResponseRef: MutableRefObject<boolean>;
  consumer: any;
  selectedAssistantInternalNameRef: MutableRefObject<string>;
  sendMessageInternal: (consumer: any, payload: {
    text: string;
    assistantInternalName: string;
    promptKey: string | null;
    includeProfile: boolean;
    clarification?: ClarificationResponse;
    chatIdOverride?: string;
    appendLocalUserMessage: boolean;
    stopSpeech: boolean;
  }) => Promise<SendMessageInternalResult>;
  onMessageSent?: (result: SendMessageInternalResult, broadcast?: boolean) => void;
  onBroadcastUserMessage?: (message: ChatMessageModel, chatId?: string | null) => void;
};

export const useChatQueue = (params: ChatQueueParams) => {
  const enqueueOutgoingMessage = useCallback((payload: {
    text: string;
    assistantInternalName: string | null;
    promptKey: string | null;
    includeProfile: boolean;
    clarification?: ClarificationResponse;
    broadcast?: boolean;
  }) => {
    const trimmedText = payload.text.trim();
    if (!trimmedText && !payload.clarification) return;

    const nextChatId = params.chatId ?? params.createChatId();
    if (!params.chatId) {
      params.setChatId(nextChatId);
    }

    const queuedId = `queued-${Date.now()}-${params.nextQueuedIdRef.current++}`;
    const createdAt = Date.now();
    const displayText = trimmedText || 'Klärungsantwort';
    params.queuedMessagesRef.current = [
      ...params.queuedMessagesRef.current,
      {
        id: queuedId,
        text: trimmedText,
        displayText,
        assistantInternalName: payload.assistantInternalName,
        promptKey: payload.promptKey,
        includeProfile: payload.includeProfile,
        clarification: payload.clarification,
        chatId: nextChatId,
        createdAt,
        broadcast: payload.broadcast ?? false,
      },
    ];
    params.setQueuedMessages((prev: any) => ([...prev, { id: queuedId, text: displayText, createdAt }]));
    params.setClarificationsToSend(undefined);
    params.setClarificationDrafts({});
    params.setClarificationOpen(false);
    params.setClarificationKey(null);
    params.setText('');
    params.setTextDraft('');
  }, [params]);

  useEffect(() => {
    params.enqueueOutgoingRef.current = enqueueOutgoingMessage;
  }, [params, enqueueOutgoingMessage]);

  const cancelQueuedMessage = useCallback((id: string) => {
    params.queuedMessagesRef.current = params.queuedMessagesRef.current.filter((item: any) => item.id !== id);
    params.setQueuedMessages((prev: any) => prev.filter((item: any) => item.id !== id));
  }, [params]);

  const flushQueuedMessage = useCallback(async () => {
    if (params.awaitingResponseRef.current) return;
    const consumer = params.consumer;
    if (!consumer) return;
    const next = params.queuedMessagesRef.current[0];
    if (!next) return;
    const assistantInternalName = next.assistantInternalName ?? params.selectedAssistantInternalNameRef.current;
    if (!assistantInternalName) return;
    params.queuedMessagesRef.current = params.queuedMessagesRef.current.slice(1);
    params.setQueuedMessages((prev: any) => prev.filter((item: any) => item.id !== next.id));
    if (next.broadcast && params.onBroadcastUserMessage) {
      const userMsg = new ChatMessageModel({
        role: "user",
        content: next.text ?? "",
        clarification: next.clarification,
      });
      params.onBroadcastUserMessage(userMsg, next.chatId);
    }
    const result = await params.sendMessageInternal(consumer, {
      text: next.text,
      assistantInternalName,
      promptKey: next.promptKey,
      includeProfile: next.includeProfile,
      clarification: next.clarification,
      chatIdOverride: next.chatId,
      appendLocalUserMessage: true,
      stopSpeech: false,
    });
    params.onMessageSent?.(result, next.broadcast);
  }, [params]);

  return { enqueueOutgoingMessage, cancelQueuedMessage, flushQueuedMessage };
};
